import {
  addBreadcrumb,
  browserSessionIntegration,
  browserTracingIntegration,
  init as sentryInit,
  makeFetchTransport,
  setTag,
  type ErrorEvent as SentryErrorEvent,
} from "@sentry/vue";
import {
  MODE,
  SENTRY_DSN,
  SENTRY_ERROR_SAMPLE_RATE,
  SENTRY_SEND_DELAY,
  SENTRY_TRACES_SAMPLE_RATE,
} from "../consts";
import { Router } from "vue-router";
import { App } from "vue";
import {
  applySessionNeutral,
  delayedTransport,
  dropNoopSessionUpdates,
} from "../../modules/shared-utils/sentry";

export interface DoNotReportError extends Error {
  readonly doNotReportError: true;
}

export class GoogleBotError extends Error implements DoNotReportError {
  public readonly doNotReportError = true;

  constructor(public readonly cause?: Error) {
    super("Page is being crawled by GoogleBot");
    this.name = "GoogleBotError";
  }
}

type MaybeDoNotReportError =
  | DoNotReportError
  | { doNotReportError?: boolean }
  | null
  | undefined;

// Console noise from scripts injected by in-app browsers (Meta's IAB:
// `FBNav*` web-vitals metrics, `hxp-*` native-bridge modules) — it repeats
// every few seconds and drowns the breadcrumb trail
const injectedConsolePattern = /^(?:FBNav|hxp-)/;
let taggedInjectedScripts = false;

// Stack-overflow spam from scripts injected by iOS in-app browsers (Chrome
// iOS, Google app): the injected script has no URL, so the whole stack is the
// single frame `at ? (undefined:<line>:<col>)`. A recursion in our own code
// always has many frames pointing into the bundle, so only drop the exact
// one-anonymous-frame shape (WEB-READER-180K)
const isInjectedScriptStackOverflow = (event: SentryErrorEvent): boolean => {
  if (event.exception?.values?.length !== 1) return false;

  const { type, value, stacktrace } = event.exception.values[0];
  if (
    type !== "RangeError" ||
    !value?.startsWith("Maximum call stack size exceeded")
  ) {
    return false;
  }

  if (stacktrace?.frames?.length !== 1) return false;

  const [frame] = stacktrace.frames;
  return (
    (frame.filename === undefined || frame.filename === "undefined") &&
    (frame.function === undefined || frame.function === "?") &&
    frame.lineno !== undefined &&
    frame.colno !== undefined
  );
};

const isExtensionRuntimeApi = (event: SentryErrorEvent): boolean => {
  if (event.exception?.values?.length !== 1) return false;

  const { type, value } = event.exception.values[0];
  return (
    type === "Error" &&
    value === "Invalid call to runtime.sendMessage(). Tab not found."
  );
};

const isKnownIgnoredError = (event: SentryErrorEvent): boolean =>
  isInjectedScriptStackOverflow(event) || isExtensionRuntimeApi(event);

export const initSentry = (app: App, router: Router) => {
  sentryInit({
    dsn: SENTRY_DSN,
    app,
    integrations: [
      browserTracingIntegration({
        router,
      }),
      // one session per page load: the default 'route' lifecycle also
      // restarts the session on vue-router's initial replaceState
      browserSessionIntegration({ lifecycle: "page" }),
    ],
    transport: (options) => {
      const transport = makeFetchTransport(options);
      return delayedTransport(
        {
          ...transport,
          send: (envelope) => transport.send(dropNoopSessionUpdates(envelope)),
        },
        SENTRY_SEND_DELAY
      );
    },
    tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
    sampleRate: SENTRY_ERROR_SAMPLE_RATE,
    debug: MODE !== "production",
    environment: MODE,
    enabled: MODE !== "development",
    attachStacktrace: true,
    // increase to capture complete URLs with their tokens
    maxValueLength: 2048,
    ignoreErrors: [
      "TypeError: cancelled", // Not even bad, just means fetch was cancelled
      "Non-Error promise rejection captured with value: cancelled",
      /AbortError/, // Doesn't even matter
      /instantSearchSDKJSBridgeClearHighlight/, // Bing search bug thing

      // Cloudflare injected script
      "Cannot read properties of null (reading 'document')",
      "TypeError: Cannot read property 'document' of null",
      /_0xh\./, // _0xh.contentWindow
    ],
    initialScope: (scope) => scope.setTag("component", "front-end"),
    beforeSend: (event, hint) => {
      if (
        (hint?.originalException as MaybeDoNotReportError)?.doNotReportError
      ) {
        return null;
      }

      if (isKnownIgnoredError(event)) {
        return null;
      }

      return applySessionNeutral(event);
    },
    beforeBreadcrumb: (breadcrumb, hint) => {
      if (
        breadcrumb.category === "console" &&
        injectedConsolePattern.test(breadcrumb.message ?? "")
      ) {
        // keep the diagnostic signal as a searchable tag instead of crumbs
        if (!taggedInjectedScripts) {
          taggedInjectedScripts = true;
          setTag("injected_scripts", "in-app-browser");
        }
        return null;
      }

      if (breadcrumb.category === "ui.click") {
        const target = hint?.event?.target;
        if (target instanceof HTMLElement) {
          const fragments: string[] = [];
          const data = (breadcrumb.data || {}) as Record<string, any>;

          const dialog = target.closest("dialog");
          if (dialog) {
            data.dialog = `${dialog.id || dialog.tagName}(${dialog.ariaLabel})`;
            fragments.push(dialog.id || dialog.ariaLabel || dialog.tagName);

            if (target === dialog) {
              fragments.push(":backdrop");
            }
          }

          const button: HTMLElement = target.closest("button, a");
          if (button) {
            data.button = `${button.tagName}(${button.title || button.ariaLabel || button.textContent?.trim()})`;
            fragments.push(
              button.title || button.ariaLabel || button.textContent?.trim()
            );
          }

          const icon = button?.querySelector(
            ".material-symbols-outlined, .material-icons"
          );
          if (icon) {
            data.icon = `${icon.className}(${icon.textContent?.trim()})`;
            fragments.push(icon.textContent?.trim() || icon.className);
          }

          if (fragments.length === 0) {
            // we didn't identify anything useful about the click
            // we could consider not reporting it at all,
            // but for now let's just report it without changing the message
            return breadcrumb;
          }

          breadcrumb.data = data;
          breadcrumb.message = fragments.join(" > ");
        }
      }
      return breadcrumb;
    },
  });

  if (window.isSecureContext === false) {
    addBreadcrumb({
      level: "warning",
      message: "isSecureContext is false, some features may not work properly",
    });
  }
};
